import { useContext, useEffect, useState } from "react";
import {
    Box, Card, Chip, CircularProgress, LinearProgress, Sheet, Stack,
    Tab, TabList, TabPanel, Tabs, Typography,
} from "@mui/joy";
import { format } from "date-fns";
import { ru } from "date-fns/locale";

import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { usePartner } from "../../PartnerContext";
import {
    APIGetBonusRules,
    APIGetBonusProgress,
    APIGetBonusHistory,
} from "../../api_partner";


const REWARD_LABELS = {
    fixed: 'Фиксированная сумма',
    percent: '% от заработанного',
    rate_increase: 'Увеличение ставки',
};

const PERIOD_LABELS = {
    one_time: 'Разово',
    monthly: 'Ежемесячно',
    quarterly: 'Ежеквартально',
};


function RewardChip({ rule }) {
    const v = Number(rule.reward_value || 0);
    let txt = '';
    if (rule.reward_type === 'fixed') txt = `+${v.toLocaleString('ru-RU')} ₽`;
    else if (rule.reward_type === 'percent') txt = `+${v}%`;
    else if (rule.reward_type === 'rate_increase') txt = `Ставка +${v}%`;
    return <Chip size="sm" color="success" variant="solid">{txt}</Chip>;
}


export default function PartnerBonus() {
    const { partnerToken: token } = usePartner();
    const [tab, setTab] = useState(0);

    const [rules, setRules] = useState([]);
    const [progress, setProgress] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [r, p, h] = await Promise.all([
                    APIGetBonusRules(token),
                    APIGetBonusProgress(token),
                    APIGetBonusHistory(token, { pageSize: 50 }),
                ]);
                setRules(r?.results || []);
                setProgress(p?.results || []);
                setHistory(h?.results || []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        if (token) load();
    }, [token]);

    if (loading) {
        return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>;
    }

    return (
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <TabList>
                <Tab>Прогресс</Tab>
                <Tab>Правила</Tab>
                <Tab>История</Tab>
            </TabList>

            {/* Прогресс */}
            <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
                {progress.length === 0 ? (
                    <Typography level="body-sm" color="neutral">Активных правил нет</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {progress.map(p => (
                            <Card
                                key={p.rule_id}
                                variant="outlined"
                                sx={{ p: 2 }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Typography level="title-sm">{p.name}</Typography>
                                            <RewardChip rule={p} />
                                            <Chip size="sm" variant="outlined">{PERIOD_LABELS[p.period] || p.period}</Chip>
                                        </Stack>
                                        {p.description && (
                                            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                                                {p.description}
                                            </Typography>
                                        )}
                                    </Box>
                                    {p.already_received_in_period && (
                                        <Chip size="sm" color="success" startDecorator={<CheckCircleRoundedIcon />}>
                                            Получено
                                        </Chip>
                                    )}
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, mb: 0.5 }}>
                                    <Typography level="body-xs" color="neutral">Прогресс</Typography>
                                    <Typography level="body-xs" sx={{ fontWeight: 600 }}>
                                        {Math.round(p.current).toLocaleString('ru-RU')} / {Math.round(p.threshold).toLocaleString('ru-RU')}
                                    </Typography>
                                </Stack>
                                <LinearProgress
                                    determinate
                                    value={p.progress_percent}
                                    color={p.progress_percent >= 100 ? 'success' : 'primary'}
                                    thickness={6}
                                />
                            </Card>
                        ))}
                    </Stack>
                )}
            </TabPanel>

            {/* Правила */}
            <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
                {rules.length === 0 ? (
                    <Typography level="body-sm" color="neutral">Правил пока нет</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {rules.map(r => (
                            <Card key={r.id} variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Typography level="title-sm">{r.name}</Typography>
                                            <RewardChip rule={r} />
                                        </Stack>
                                        {r.description && (
                                            <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                                                {r.description}
                                            </Typography>
                                        )}
                                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                            <Chip size="sm" variant="outlined">{PERIOD_LABELS[r.period]}</Chip>
                                            <Chip size="sm" variant="soft">
                                                Порог: {Number(r.threshold).toLocaleString('ru-RU')}
                                            </Chip>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Card>
                        ))}
                    </Stack>
                )}
            </TabPanel>

            {/* История */}
            <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
                {history.length === 0 ? (
                    <Typography level="body-sm" color="neutral">Бонусов пока не было</Typography>
                ) : (
                    <Stack spacing={1}>
                        {history.map(h => (
                            <Sheet
                                key={h.id}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 'sm' }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography level="title-sm" color="success">
                                            +{Number(h.amount).toLocaleString('ru-RU')} ₽
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {h.rule_name || h.description || 'Бонус'}
                                            {' • '}
                                            {format(new Date(h.date_create), 'd MMM yyyy, HH:mm', { locale: ru })}
                                        </Typography>
                                    </Box>
                                    {h.is_manual && (
                                        <Chip size="sm" variant="outlined">Ручное</Chip>
                                    )}
                                </Stack>
                            </Sheet>
                        ))}
                    </Stack>
                )}
            </TabPanel>
        </Tabs>
    );
}
